import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Observable, map} from 'rxjs';
import {environment} from '@environment/environment';

export type AccountStatus = 'active' | 'invited' | 'disabled';

export interface Account {
  id: number;
  email: string;
  name: string;
  lastname: string | null;
  role: number;
  company: number;
  company_name: string;
  status: AccountStatus;
  created_at: string;
  /** Presente solo subito dopo la creazione o un nuovo invito */
  invite_link?: string;
}

export interface AccountInput {
  email?: string;
  name: string;
  lastname: string;
  role: number;
  company?: number;
}

export interface ClientCompany {
  id: number;
  name: string;
  vat_number: string;
  fiscal_code: string;
  email: string;
  pec?: string | null;
  registered_office_city: string;
  registered_office_province: string;
  registered_office_address: string;
  registered_office_cap: string;
  enabled: boolean;
  users_count: number;
  documents_count: number;
}

type Envelope<T> = { success: boolean; data: T };

/**
 * API del back office (utenti e aziende). Usa HttpClient senza il retry di ApiService:
 * una POST ripetuta in automatico potrebbe creare due volte lo stesso utente.
 */
@Injectable({providedIn: 'root'})
export class AdminApiService {
  private http = inject(HttpClient);
  private url = environment.api.base_url;

  listAccounts(company?: number): Observable<Account[]> {
    const params = company ? new HttpParams().set('company', company) : undefined;
    return this.http.get<Envelope<Account[]>>(`${this.url}/users/accounts`, {params}).pipe(map((r) => r.data));
  }

  createAccount(body: AccountInput): Observable<Account> {
    return this.http.post<Envelope<Account>>(`${this.url}/users/accounts`, body).pipe(map((r) => r.data));
  }

  updateAccount(id: number, body: Partial<AccountInput>): Observable<Account> {
    return this.http.patch<Envelope<Account>>(`${this.url}/users/accounts/${id}`, body).pipe(map((r) => r.data));
  }

  accountAction(id: number, action: 'deactivate' | 'activate' | 'invite'): Observable<Account> {
    return this.http.post<Envelope<Account>>(`${this.url}/users/accounts/${id}/${action}`, {}).pipe(map((r) => r.data));
  }

  listCompanies(): Observable<ClientCompany[]> {
    return this.http.get<Envelope<ClientCompany[]>>(`${this.url}/companies/manage`).pipe(map((r) => r.data));
  }

  createCompany(body: Partial<ClientCompany>): Observable<ClientCompany> {
    return this.http.post<Envelope<ClientCompany>>(`${this.url}/companies/manage`, body).pipe(map((r) => r.data));
  }

  updateCompany(id: number, body: Partial<ClientCompany>): Observable<ClientCompany> {
    return this.http.patch<Envelope<ClientCompany>>(`${this.url}/companies/manage/${id}`, body).pipe(map((r) => r.data));
  }
}

/** Messaggio leggibile da una risposta di errore del backend ({data: [{campo: {msg}}]} o {message}). */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const body = (error as HttpErrorResponse)?.error;
  if (Array.isArray(body?.data)) {
    const messages = body.data.flatMap((item: Record<string, {msg?: string}>) =>
      Object.values(item).map((v) => v?.msg).filter(Boolean));
    if (messages.length) {
      return messages.join(' ');
    }
  }
  return typeof body?.message === 'string' && body.message && body.message !== 'validation_error' ? body.message : fallback;
}
